import {
  SpaceBetween,
  PieChart,
  Button,
  StatusIndicator,
  Box,
  ExpandableSection,
} from '@cloudscape-design/components';
import Divider from 'src/components/divider/Divider';
import AuditQuestionFilterView from '../AuditQuestionFilterView';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { filter } from 'lodash';
import { useQuestionResponses } from 'src/store/questionResponses/useQuestionResponses';
import { getAuditQuestionResponseStatusIcon } from 'src/utils/generalUtils';
import { DEFAULT_LANGUAGE, QuestionResultType } from 'src/contstant/commonLabels';
import useUserProfile from 'src/hooks/useUserProfile';
import { SearchAuditQuestionsRequest } from 'src/types/helios/SearchAuditQuestions';
import {
  colorBackgroundButtonLinkActive,
  colorTextBodySecondary,
  spaceScaledXxs,
} from '@cloudscape-design/design-tokens';
import { getQuestionContent } from 'src/helper/questionHelper';
import {
  QUESTION_RESPONSE_COLORS,
  QUESTION_RESPONSE_LABELS,
} from 'src/contstant/questionResponseColors';
import {
  getQuestionViolations,
  hasSubQuestionViolation,
  getWarningTooltip,
} from 'src/utils/auditClosureValidationUtils';
import { Icon } from '@cloudscape-design/components';
import { Question } from 'src/types/helios/Question';
import { AuditQuestionItemResponse } from 'src/types/helios/QuestionResponse';
import {
  extractTagsFromQuestions,
  getAnsweredCount,
  groupQuestionsByTagValue,
} from 'src/utils/auditTagUtils';
import TagGroupSelector from './TagGroupSelector';
import { useListReasonsQuery } from 'src/data/api/helios-service-ts/reason/queries/useListReasonsQuery';
import { ReasonCategory } from '@amzn/gbsa-helios-typescript-client';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
// Define component props interface
interface PerformAuditQuestionsListProps {
  auditId: string;
  businessUnitId: string;
}

// Main component definition
const PerformAuditQuestionsList = ({
  auditId,
  businessUnitId,
}: PerformAuditQuestionsListProps) => {
  const { getUserSelectedLanguage, getPreferLanguageList } = useUserProfile();

  const { enableEnhancedResponseTypes } = useFeatureFlags();
  // State to control filter visibility
  const [showFilters, setShowFilters] = useState(false);
  // State to track selected pie chart filter
  const [selectedPieFilter, setSelectedPieFilter] = useState<string | null>(null);
  // State to track hovered pie chart segment for highlighting
  const [hoveredPieSegment, setHoveredPieSegment] = useState<string | null>(null);
  // State for the selected tag group to group questions by
  const [groupByTagGroup, setGroupByTagGroup] = useState<string | null>(null);

  // Create ref array to store DOM references for each question
  const questionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize search request with audit and language data
  const initSearchRequest: SearchAuditQuestionsRequest = {
    businessUnitId: businessUnitId,
    auditId: auditId,
    language: DEFAULT_LANGUAGE,
  };

  // State for search parameters
  const [searchParams, setSearchParams] =
    useState<SearchAuditQuestionsRequest>(initSearchRequest);

  // Destructure question responses hook
  const {
    auditQuestionResponses,
    currentQuestionIndex,
    setCurrentQuestion,
    searchAuditQuestions,
    auditQuestionsData,
    setFilteredQuestions,
  } = useQuestionResponses();

  // Fetch NA reasons for THIS audit's division (matches the dropdown's scope)
  // so the side panel warnings stay consistent with the question view dropdowns.
  const auditDivisionName = auditQuestionsData?.auditMetadata?.divisionName ?? '';
  const { data: reasonsData } = useListReasonsQuery(
    {
      businessUnitId,
      category: ReasonCategory.NA_REASON,
      divisionName: auditDivisionName,
      activeOnly: true,
    },
    { enabled: !!auditDivisionName },
  );
  const naReasonsExist = (reasonsData?.reasons ?? []).length > 0;

  // Effect to search questions when search params change
  useEffect(() => {
    searchAuditQuestions({
      ...searchParams,
      additionalLanguages: getPreferLanguageList(),
    });
    // Reset to first question when filters change
    setCurrentQuestion(0);
  }, [searchParams]);

  // Effect to auto-scroll to selected question when index changes
  useEffect(() => {
    // Check if the current question element exists
    if (questionRefs.current[currentQuestionIndex]) {
      // Scroll to the selected question with smooth animation
      questionRefs.current[currentQuestionIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [currentQuestionIndex]);

  // Extract questions array from audit data and apply client-side filtering if needed
  const auditQuestions = useMemo(() => {
    const allQuestions = auditQuestionsData.questions;

    // If no pie chart filter is selected, return all questions
    if (!selectedPieFilter) {
      return allQuestions;
    }

    // Apply client-side filtering based on selected filter
    return allQuestions.filter((question) => {
      const response = auditQuestionResponses.find(
        (r) => r.questionId === question.questionId,
      );

      switch (selectedPieFilter) {
        case QUESTION_RESPONSE_LABELS.PASS:
          return response?.responseResult === QuestionResultType.PASS;
        case QUESTION_RESPONSE_LABELS.FAIL:
          return response?.responseResult === QuestionResultType.FAIL;
        case QUESTION_RESPONSE_LABELS.NA:
          return response?.responseResult === QuestionResultType.NA;
        case QUESTION_RESPONSE_LABELS.UNANSWERED:
          return !response?.responseResult || response?.responseResult === '';
        default:
          return true;
      }
    });
  }, [auditQuestionsData.questions, selectedPieFilter, auditQuestionResponses]);

  // Effect to update filtered question IDs in Redux for the left panel to use
  useEffect(() => {
    if (selectedPieFilter) {
      const filteredIds = auditQuestions.map((q) => q.questionId);
      setFilteredQuestions(filteredIds);
    } else {
      setFilteredQuestions(null);
    }
  }, [auditQuestions, selectedPieFilter, setFilteredQuestions]);

  // Extract audit-scoped tags from the loaded questions.
  // For a given audit, server-side filters always return subsets of the same
  // question pool, so the tag groups are stable — no accumulation needed.
  const auditScopedTags = useMemo(
    () => extractTagsFromQuestions(auditQuestionsData.questions),
    [auditQuestionsData.questions],
  );

  // Available tag group names for the "Group By" dropdown
  const tagGroupOptions = useMemo(
    () =>
      auditScopedTags.map((tag) => ({
        label: tag.tagGroupName!,
        value: tag.tagGroupName!,
      })),
    [auditScopedTags],
  );

  // Group questions by selected tag group value
  const groupedQuestions = useMemo(() => {
    if (!groupByTagGroup) return null;
    return groupQuestionsByTagValue(auditQuestions, groupByTagGroup);
  }, [auditQuestions, groupByTagGroup]);

  // Build a flat index map so clicking a question in a group sets the correct index
  // in the overall auditQuestions array (needed for setCurrentQuestion)
  const questionIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    auditQuestions.forEach((q, idx) => {
      map.set(q.questionId, idx);
    });
    return map;
  }, [auditQuestions]);

  // Lookup map for question responses by questionId (O(1) instead of O(n) find per render)
  const questionResponseMap = useMemo(() => {
    const map = new Map<string, AuditQuestionItemResponse>();
    auditQuestionResponses?.forEach((response) => {
      map.set(response.questionId, response);
    });
    return map;
  }, [auditQuestionResponses]);

  /**
   * Handles pie chart segment click to filter questions
   * @param segmentTitle - The title of the clicked segment (Pass, Fail, N/A, Unanswered)
   */
  const handlePieChartClick = useCallback((segmentTitle: string) => {
    // Toggle filter: if same segment clicked, clear filter; otherwise set new filter
    setSelectedPieFilter((prev) => (prev === segmentTitle ? null : segmentTitle));
  }, []);

  // Store current filters from AuditQuestionFilterView
  const [filters, setFilters] = useState<Partial<SearchAuditQuestionsRequest>>({});

  /**
   * Effect to update search params when pie chart filter changes
   */
  useEffect(() => {
    let completionStatus: string[] | undefined = undefined;

    if (selectedPieFilter) {
      switch (selectedPieFilter) {
        case QUESTION_RESPONSE_LABELS.PASS:
          completionStatus = [QuestionResultType.PASS];
          break;
        case QUESTION_RESPONSE_LABELS.FAIL:
          completionStatus = [QuestionResultType.FAIL];
          break;
        case QUESTION_RESPONSE_LABELS.NA:
          completionStatus = [QuestionResultType.NA];
          break;
        case QUESTION_RESPONSE_LABELS.UNANSWERED:
          // For unanswered questions, we pass an empty array or special marker
          // The backend should interpret this as questions without responses
          completionStatus = [];
          break;
      }
    }

    const newSearchParams = {
      ...initSearchRequest,
      ...filters,
      completionStatus,
    };
    setSearchParams(newSearchParams);
  }, [selectedPieFilter, filters]);

  // Memoized function to calculate response counts
  const getCounts = useCallback(() => {
    // Count questions with PASS result
    const passCount = filter(
      auditQuestionResponses,
      (q) => q.responseResult === QuestionResultType.PASS,
    ).length;
    // Count questions with FAIL result
    const failCount = filter(
      auditQuestionResponses,
      (q) => q.responseResult === QuestionResultType.FAIL,
    ).length;
    // Count questions with N/A result
    const naCount = filter(
      auditQuestionResponses,
      (q) => q.responseResult === QuestionResultType.NA,
    ).length;
    // Count unanswered questions
    const unanswserdCount = filter(
      auditQuestionResponses,
      (q) => q.responseResult === undefined || q.responseResult === '',
    ).length;
    return { passCount, failCount, naCount, unanswserdCount };
  }, [auditQuestionResponses]);

  // Destructure count values
  const { passCount, failCount, naCount, unanswserdCount } = getCounts();
  // Calculate total answered questions
  const totalAnswerd = passCount + failCount + naCount;
  // Get total questions count from metadata
  const totalQuestions = auditQuestionsData?.auditMetadata?.metrics?.totalQuestions ?? 0;

  // Memoized pie chart data to ensure consistent object references
  const pieChartData = useMemo(
    () => [
      {
        title: QUESTION_RESPONSE_LABELS.PASS,
        value: passCount || 0,
        color: QUESTION_RESPONSE_COLORS.PASS,
      },
      {
        title: QUESTION_RESPONSE_LABELS.FAIL,
        value: failCount || 0,
        color: QUESTION_RESPONSE_COLORS.FAIL,
      },
      {
        title: QUESTION_RESPONSE_LABELS.NA,
        value: naCount || 0,
        color: QUESTION_RESPONSE_COLORS.NA,
      },
      {
        title: QUESTION_RESPONSE_LABELS.UNANSWERED,
        value: unanswserdCount || 0,
        color: QUESTION_RESPONSE_COLORS.UNANSWERED,
      },
    ],
    [passCount, failCount, naCount, unanswserdCount],
  );

  // Get the highlighted segment based on selected filter or hovered segment
  const highlightedSegment = useMemo(() => {
    const activeSegment = selectedPieFilter || hoveredPieSegment;
    if (!activeSegment) return null;
    return pieChartData.find((segment) => segment.title === activeSegment) || null;
  }, [selectedPieFilter, hoveredPieSegment, pieChartData]);

  // Render component JSX
  return (
    // Main container with vertical spacing
    <SpaceBetween
      data-testid="perform-audit-questions-list"
      size="m"
      direction="vertical"
    >
      {/* Render pie chart if metrics are available */}
      {auditQuestionsData.auditMetadata.metrics && (
        <>
          <PieChart
            data-testid="audit-progress-pie-chart"
            data={pieChartData}
            ariaDescription="Donut chart showing audit progress."
            ariaLabel="Donut chart for audit progress"
            innerMetricDescription="Total Questions"
            innerMetricValue={`${totalAnswerd}/${totalQuestions}`}
            size="medium"
            variant="donut"
            hideLegend={true}
            hideFilter={true}
            fitHeight
            highlightedSegment={highlightedSegment}
            empty={
              <Box
                data-testid="pie-chart-empty-state"
                margin={{ vertical: 'xs' }}
                textAlign="center"
                color="inherit"
              >
                <SpaceBetween data-testid="empty-state-container" size="m">
                  <StatusIndicator data-testid="no-progress-indicator" type="warning">
                    No Progress
                  </StatusIndicator>
                </SpaceBetween>
              </Box>
            }
          />
          {/* Clickable filter legend items with hover highlighting */}
          <Box data-testid="pie-chart-legend-filters" textAlign="center">
            <SpaceBetween size="s" direction="horizontal" alignItems="center">
              {[
                {
                  key: QUESTION_RESPONSE_LABELS.PASS,
                  color: QUESTION_RESPONSE_COLORS.PASS,
                  count: passCount,
                },
                {
                  key: QUESTION_RESPONSE_LABELS.FAIL,
                  color: QUESTION_RESPONSE_COLORS.FAIL,
                  count: failCount,
                },
                {
                  key: QUESTION_RESPONSE_LABELS.NA,
                  color: QUESTION_RESPONSE_COLORS.NA,
                  count: naCount,
                },
                {
                  key: QUESTION_RESPONSE_LABELS.UNANSWERED,
                  color: QUESTION_RESPONSE_COLORS.UNANSWERED,
                  count: unanswserdCount,
                },
              ].map(({ key, color, count }) => {
                const isSelected = selectedPieFilter === key;
                const isGrayedOut = selectedPieFilter !== null && !isSelected;
                return (
                  <div
                    key={key}
                    data-testid={`filter-${key.toLowerCase()}-button`}
                    onMouseEnter={() => setHoveredPieSegment(key)}
                    onMouseLeave={() => setHoveredPieSegment(null)}
                    onClick={() => handlePieChartClick(key)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: spaceScaledXxs,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '14px',
                        height: '14px',
                        backgroundColor: isGrayedOut ? colorTextBodySecondary : color,
                      }}
                    />
                    <Box
                      fontWeight={isSelected ? 'bold' : 'normal'}
                      color={isGrayedOut ? 'text-status-inactive' : 'text-body-secondary'}
                    >
                      {key} ({count})
                    </Box>
                  </div>
                );
              })}
            </SpaceBetween>
          </Box>
        </>
      )}

      {/* Visual separator */}
      <Divider data-testid="first-divider" />

      {/* Group By tag group selector - always visible above filters */}
      <TagGroupSelector
        options={tagGroupOptions}
        selectedTagGroup={groupByTagGroup}
        onChange={setGroupByTagGroup}
      />

      {/* Filter controls */}
      <SpaceBetween data-testid="filter-controls" size="xs" direction="horizontal">
        <Button
          data-testid="filter-button"
          variant="normal"
          iconName={showFilters ? 'angle-up' : 'angle-down'}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </SpaceBetween>

      {/* Conditionally render filter view */}
      {showFilters && (
        <AuditQuestionFilterView
          data-testid="audit-question-filter-view"
          onFilterChange={function (newFilters: SearchAuditQuestionsRequest): void {
            // Store filters separately to preserve pie chart filter
            setFilters({
              keyword: newFilters.keyword,
              questionId: newFilters.questionId,
              categories: newFilters.categories,
              tagFilters: newFilters.tagFilters,
            });

            // Merge with existing completion status from pie chart filter
            const mergedFilters = {
              ...newFilters,
              completionStatus: searchParams.completionStatus,
            };
            setSearchParams(mergedFilters);
          }}
          initSearchRequest={searchParams}
          defaultSearchRequest={initSearchRequest}
          auditScopedTags={auditScopedTags}
        />
      )}
      {/* Another visual separator */}
      <Divider data-testid="second-divider" />

      {/* Questions list container - grouped or flat */}
      <SpaceBetween data-testid="questions-list-container" size="xs">
        {groupedQuestions
          ? // Grouped rendering: questions organized by tag value sections
            Array.from(groupedQuestions.entries()).map(([tagValue, groupQuestions]) => {
              if (groupQuestions.length === 0) return null;
              const answeredCount = getAnsweredCount(groupQuestions, questionResponseMap);
              return (
                <ExpandableSection
                  key={tagValue}
                  data-testid={`tag-group-section-${tagValue}`}
                  variant="default"
                  defaultExpanded
                  headerText={`${tagValue} (${answeredCount}/${groupQuestions.length})`}
                >
                  <SpaceBetween size="xs">
                    {groupQuestions.map((q, i) => {
                      const globalIndex = questionIndexMap.get(q.questionId) ?? 0;
                      return renderQuestionItem(
                        q,
                        globalIndex,
                        i === groupQuestions.length - 1,
                      );
                    })}
                  </SpaceBetween>
                </ExpandableSection>
              );
            })
          : // Flat rendering: original question list
            auditQuestions.map((q, index: number) =>
              renderQuestionItem(q, index, index === auditQuestions.length - 1),
            )}
      </SpaceBetween>
    </SpaceBetween>
  );

  /**
   * Renders a single question item with status indicator, text, warnings, and sub-questions.
   * Shared between flat and grouped views.
   */
  function renderQuestionItem(q: Question, index: number, isLast: boolean) {
    const questionResponse = questionResponseMap.get(q.questionId);
    const questionViolations = getQuestionViolations(
      q,
      questionResponse,
      naReasonsExist,
      enableEnhancedResponseTypes,
    );
    return (
      <div
        key={q.questionId}
        data-testid={`question-container-${q.questionId}`}
        ref={(el) => (questionRefs.current[index] = el)}
      >
        {/* Clickable question item */}
        <div
          data-testid={`question-item-${q.questionId}`}
          onClick={() => setCurrentQuestion(index)}
          style={{
            cursor: 'pointer',
            backgroundColor:
              index === currentQuestionIndex
                ? colorBackgroundButtonLinkActive
                : 'transparent',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <StatusIndicator
            data-testid={`question-status-${q.questionId}`}
            type={getAuditQuestionResponseStatusIcon(questionResponse?.responseResult)}
          />
          <p data-testid={`question-text-${q.questionId}`}>
            <strong>
              {q.questionId}
              {':  '}
            </strong>
            {getQuestionContent(q, getUserSelectedLanguage()).questionDetails.title}
          </p>
          {questionViolations.length > 0 && (
            <span
              data-testid={`question-warning-${q.questionId}`}
              title={getWarningTooltip(questionViolations)}
              style={{ marginLeft: '4px', flexShrink: 0 }}
            >
              <Icon name="status-warning" variant="warning" />
            </span>
          )}
        </div>
        {/* Sub-questions when this question is selected */}
        {index === currentQuestionIndex &&
          getQuestionContent(q, getUserSelectedLanguage()).subQuestions &&
          getQuestionContent(q, getUserSelectedLanguage()).subQuestions!.length > 0 && (
            <div
              data-testid={`sub-questions-container-${q.questionId}`}
              style={{ marginLeft: '16px', marginTop: '4px' }}
            >
              {(() => {
                const allSubs = getQuestionContent(
                  q,
                  getUserSelectedLanguage(),
                ).subQuestions!;
                const isConditional =
                  enableEnhancedResponseTypes && q.hasConditionalSubQuestions;
                const parentResult = questionResponse?.responseResult;

                const displayedSubs =
                  isConditional && parentResult && parentResult !== 'NA'
                    ? allSubs.filter((subQ) => {
                        const subResp = questionResponse?.subResponses?.find(
                          (sr) => sr.subQuestionId === subQ.subQuestionId,
                        );
                        return (
                          subResp?.showWhenParentIs === parentResult ||
                          subResp?.showWhenParentIs === 'BOTH'
                        );
                      })
                    : isConditional
                      ? []
                      : allSubs;

                return displayedSubs.map((subQ, subIndex) => (
                  <div
                    key={subIndex}
                    data-testid={`sub-question-${q.questionId}-${subIndex}`}
                    style={{
                      padding: '4px 8px',
                      fontSize: '14px',
                      color: '#666',
                      borderLeft: `3px solid ${colorBackgroundButtonLinkActive}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                    }}
                  >
                    <StatusIndicator
                      data-testid={`sub-question-status-${q.questionId}-${subIndex}`}
                      type={getAuditQuestionResponseStatusIcon(
                        (() => {
                          const subResp = questionResponse?.subResponses?.find(
                            (item) => item.subQuestionId === subQ.subQuestionId,
                          );
                          // ERT: data-collection subs use responseValue for answered status
                          if (
                            enableEnhancedResponseTypes &&
                            subQ.responseType &&
                            subQ.responseType !== 'PASS_FAIL'
                          ) {
                            return subResp?.responseValue ? 'PASS' : undefined;
                          }
                          return subResp?.result;
                        })(),
                      )}
                    />
                    <p>
                      <strong>
                        {subQ.subQuestionId}
                        {':  '}
                      </strong>
                      {subQ.title}
                    </p>
                    {subQ.subQuestionId &&
                      hasSubQuestionViolation(questionViolations, subQ.subQuestionId) && (
                        <span
                          data-testid={`sub-question-warning-${q.questionId}-${subIndex}`}
                          title={(() => {
                            const violation = questionViolations.find(
                              (v) => v.subQuestionId === subQ.subQuestionId,
                            );
                            if (violation?.missingResponse) {
                              return 'Sub-question response is required.';
                            }
                            if (violation?.missingNaReason) {
                              return 'Not Applicable reason is required.';
                            }
                            return 'Comment and/or attachment required for this sub-question.';
                          })()}
                          style={{ marginLeft: '4px', flexShrink: 0 }}
                        >
                          <Icon name="status-warning" variant="warning" />
                        </span>
                      )}
                  </div>
                ));
              })()}
            </div>
          )}
        {/* Divider between questions (not after last in current view) */}
        {!isLast && <Divider />}
      </div>
    );
  }
};

// Export component as default
export default PerformAuditQuestionsList;
